import { useState, useMemo } from "react";
import { t } from "@lingui/core/macro";
import { adminUrls, machineUrls } from "_/routes";
import { indexById } from "_/utils";

import { Channel, useChannelMachines } from "_/data/channels";
import { Buildset } from "_/data/buildsets";
import { Machine } from "_/data/machines";
import {
  Table,
  ColumnDef,
  CustomCellLink,
  CustomCellStatic,
} from "_/components/table";
import { formatBuildsetLabel } from "_/utils/buildset-display";
import { BuildsetTooltip } from "_/components/buildsetTooltip";

import * as S from "./styled";

function MachinesCell({ channelName }: { channelName: string }) {
  const { data: machines = [] } = useChannelMachines(channelName);

  const [showAll, setShowAll] = useState<boolean>(false);

  // Filter out unattached and sort by serial ascending
  const attached = machines.filter((m: Machine) => m.hardwareAttached);
  const sorted = attached.toSorted((a, b) =>
    a.hardware.serial.localeCompare(b.hardware.serial, undefined, {
      numeric: true,
    })
  );

  const displayMachines = showAll ? sorted : sorted.slice(0, 5);
  const extraCount = Math.max(sorted.length - 5, 0);
  const canToggle = extraCount > 0;

  return (
    <S.MachineListWrapper>
      <S.MachineList>
        {displayMachines.map((m: Machine) => {
          const serial = m.hardware.serial;
          const label = m.name !== serial ? `${m.name} (${serial})` : serial;
          return (
            <S.MachineLink key={m.id} to={machineUrls.index(m.id)}>
              {label}
            </S.MachineLink>
          );
        })}
      </S.MachineList>
      {canToggle && (
        <S.ToggleLink onClick={() => setShowAll((p) => !p)}>
          {showAll ? t`common.less` : `+${extraCount} ${t`common.more`}`}
        </S.ToggleLink>
      )}
    </S.MachineListWrapper>
  );
}

type ChannelsTableProps = {
  channels: Channel[];
  buildsets: Buildset[];
  isLoading: boolean;
};

export const ChannelsTable = ({
  channels,
  buildsets,
  isLoading,
}: ChannelsTableProps) => {
  const buildsetMap = useMemo(() => indexById(buildsets), [buildsets]);

  const columns: ColumnDef<Channel>[] = [
    { id: "name", header: t`common.name`, accessorKey: "name" },
    {
      id: "version",
      header: t`common.version`,
      accessorKey: "targetBuildset",
      meta: { customTd: true },
      cell: ({
        row: {
          original: { targetBuildset },
        },
      }) => {
        if (!targetBuildset) {
          return <CustomCellStatic>{t`common.unknown`}</CustomCellStatic>;
        }
        const buildset = buildsetMap[targetBuildset];
        if (!buildset) {
          return <CustomCellStatic>{targetBuildset}</CustomCellStatic>;
        }
        const { value, fullName, id } = formatBuildsetLabel(buildset);
        return (
          <CustomCellLink to={adminUrls.buildset(buildset.id)}>
            <BuildsetTooltip value={value} fullName={fullName} id={id}>
              {value}
            </BuildsetTooltip>
          </CustomCellLink>
        );
      },
    },
    {
      id: "totalMachines",
      header: t`common.machines`,
      accessorKey: "totalMachines",
      cell: ({
        row: {
          original: { name },
        },
      }) => <MachinesCell channelName={name} />,
    },
  ];

  return (
    <Table
      columns={columns}
      data={channels}
      loading={isLoading}
      initialState={{
        sorting: [
          { id: "totalMachines", desc: true },
          { id: "name", desc: false },
        ],
      }}
    />
  );
};
